import React, { useEffect, useMemo, useState } from "react"
import { Box, Paper, Typography, useMediaQuery } from "@mui/material"
import { getGridDateOperators } from "@mui/x-data-grid"
import dayjs from "dayjs"
import CustomFilterBoolean from "src/components/custom-filter-boolean"
import PriceService from "src/services/price-service"
import { TokenService } from "src/services/token-service"
import { DATE_FORMAT } from "src/utils/constants"
import { formatDateString } from "src/utils/format-date"
import DatePickerSelectV2 from "src/v2/components/Date/date-picker-select"
import DataGridTable from "src/v2/components/Table/data-grid-table"
import ButtonWithIcon from "src/v2/components/Button/Button"
import { dispatchSnackbar } from "src/v2/components/SnackBar/snackbar-bus"
import SideBarLayout from "src/v2/layout/side-bar-layout"

const TITLE = "Preços Log"
const QUERY_ERROR = "Não foi possível carregar os logs de preços."
const EMPTY_DETAIL = "Não existem dados para essa data ou usuário"
const COLUMNS = [
    {
        field: "DataReferencia",
        headerName: "Data Referência",
        minWidth: 150,
        type: "date",
        valueGetter: ({ value }) => value ? dayjs(value).toDate() : null,
        valueFormatter: ({ value }) => formatDateString(value),
        filterOperators: getGridDateOperators(),
    },
    {
        field: "CETIP",
        headerName: "CETIP",
        minWidth: 250,
        flex: 1,
        renderCell: ({ value }) => <span title={value}>{value}</span>,
    },
    {
        field: "Inclusao",
        headerName: "Inclusão",
        minWidth: 110,
        flex: 1,
        valueFormatter: ({ value }) => value ? "Sim" : "Não",
        filterOperators: CustomFilterBoolean({}),
    },
    {
        field: "Exclusao",
        headerName: "Exclusão",
        minWidth: 110,
        flex: 1,
        valueFormatter: ({ value }) => value ? "Sim" : "Não",
        filterOperators: CustomFilterBoolean({}),
    },
    {
        field: "Observacao",
        headerName: "Observação",
        minWidth: 500,
        flex: 1,
        renderCell: ({ value }) => <span title={value}>{value}</span>,
    },
]

const getPreviousBusinessDay = () => {
    let date = dayjs().subtract(1, "day")
    while (date.day() === 0 || date.day() === 6) {
        date = date.subtract(1, "day")
    }
    return date.format(DATE_FORMAT)
}

const CustomersPriceLogPage = () => {
    const lowViewport = useMediaQuery("(max-height: 600px)")
    const priceService = useMemo(() => new PriceService(), [])
    const [selectedDate, setSelectedDate] = useState(getPreviousBusinessDay)
    const [dateError, setDateError] = useState("")
    const [rows, setRows] = useState([])
    const [status, setStatus] = useState("loading")
    const [retryVersion, setRetryVersion] = useState(0)

    useEffect(() => {
        let ignoreResponse = false
        const loadPrices = async () => {
            setStatus("loading")
            setRows([])
            try {
                const { user } = new TokenService().decodeToken()
                const response = await priceService.getCustumersPricesLog(user.IDCliente, selectedDate)
                if (ignoreResponse) return

                const { data } = response.body
                if (!Array.isArray(data)) throw new Error("Resposta inválida")

                setRows(data
                    .filter((row) => dayjs(row.DataReferencia).format(DATE_FORMAT) === selectedDate)
                    .map((row, id) => ({ ...row, id })))
                setStatus("success")
            } catch (error) {
                if (ignoreResponse) return
                if (error.status === 422 && error.response?.body?.detail === EMPTY_DETAIL) {
                    setStatus("success")
                    return
                }
                setStatus("error")
                dispatchSnackbar(QUERY_ERROR, "error")
            }
        }
        loadPrices()
        return () => { ignoreResponse = true }
    }, [priceService, selectedDate, retryVersion])

    const changeDate = (value, context) => {
        const parsedDate = dayjs(value)
        if (!value || !parsedDate.isValid() || context?.validationError) {
            setDateError("Informe uma data válida.")
            return
        }
        setDateError("")
        setSelectedDate(parsedDate.format(DATE_FORMAT))
    }

    return (
        <SideBarLayout title={TITLE} header headerTitle={TITLE} variant="v1"
            disablePageScroll={!lowViewport} headerImage="/background/eventos_headers.jpg" headerPosition="1% 42%">
            <Box sx={{
                mt: -2,
                height: lowViewport ? 400 : { xxxs: "calc(100dvh - 262px)", lg: "calc(100dvh - 202px)" },
                minHeight: 0,
                pb: 1,
                boxSizing: "border-box",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                gap: 2,
            }}>
                <Paper sx={{ p: 3, flexShrink: 0 }}>
                    <Box sx={{ width: { xxxs: "100%", sm: 260 } }}>
                        <DatePickerSelectV2 value={selectedDate} label="Data de Exibição"
                            onChange={changeDate} error={Boolean(dateError)} helperText={dateError || undefined} />
                    </Box>
                </Paper>
                <Paper sx={{ flex: 1, p: 3, minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}>
                    {status === "error" && (
                        <Box role="alert" sx={{ pb: 2, flexShrink: 0 }}>
                            <Typography sx={{ color: "error.light" }}>{QUERY_ERROR}</Typography>
                            <ButtonWithIcon size="md" sx={{ mt: 1 }} disabled={Boolean(dateError)}
                                onClick={() => setRetryVersion((version) => version + 1)}>
                                Tentar novamente
                            </ButtonWithIcon>
                        </Box>
                    )}
                        <DataGridTable rows={rows} columns={COLUMNS} contained
                            loading={status === "loading"} resetKey={selectedDate}
                            manualClientDataOperations autoRowHeight rowPadding={2} scrollbarSize={16}
                            disableRowSelectionOnClick sortingOrder={["asc", "desc"]}
                            sx={{
                                "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus-within": {
                                    outline: (theme) => `2px solid ${theme.palette.primary.main3} !important`,
                                    outlineOffset: "-2px",
                                },
                            }} />
                </Paper>
            </Box>
        </SideBarLayout>
    )
}

export default CustomersPriceLogPage
